import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconBuildingHospital,
  IconCalendarEvent,
  IconCheck,
  IconHomeHeart,
  IconMailCheck,
  IconMapPin,
  IconShieldCheck,
  IconStethoscope,
} from "@tabler/icons-react";
import { NextGenBrand } from "@evolys/ui";
import {
  AppointmentRequesterRelationship,
  AppointmentRequestedItemType,
  CollectionAppointmentStatus,
  CollectionAppointmentType,
  type AppointmentEstimateDto,
  type AppointmentEstimateItemInput,
  type CreatePublicAppointmentRequest,
  type PublicAppointmentAccessDto,
  type PublicAppointmentConfigurationDto,
  type PublicAppointmentCreatedDto,
  type PublicAppointmentSlotDto,
  type PublicAppointmentStatusDto,
  type PublicMarketingSiteDto,
} from "@evolys/types";
import {
  PATIENT_SEX_OPTIONS,
  amountIncludingTax,
  resetLocationForAppointmentType,
  updateRequesterEmail,
} from "./bookingFormState";
import {
  addCalendarDays,
  calendarWeek,
  formatCalendarDay,
  formatCalendarWeekRange,
  startOfCalendarWeek,
} from "./bookingCalendar";

type BookingPageProps = {
  site: PublicMarketingSiteDto;
  style: React.CSSProperties;
};

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
      theme: "auto";
      size: "flexible";
    },
  ) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type BookingForm = {
  type: CollectionAppointmentType;
  homeServiceAreaId: string;
  officeLocationId: string;
  slotId: string;
  relationship: AppointmentRequesterRelationship;
  requesterFirstName: string;
  requesterLastName: string;
  requesterEmail: string;
  requesterPhone: string;
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  patientSex: string;
  patientEmail: string;
  patientPhone: string;
  receiptEmail: string;
  addressLine1: string;
  addressLine2: string;
  community: string;
  accessDirections: string;
  clinicalNotes: string;
  selectedItemKeys: string[];
  discussAtVisit: boolean;
  authorityAttested: boolean;
};

const initialForm: BookingForm = {
  type: CollectionAppointmentType.HomeCollection,
  homeServiceAreaId: "",
  officeLocationId: "",
  slotId: "",
  relationship: AppointmentRequesterRelationship.Self,
  requesterFirstName: "",
  requesterLastName: "",
  requesterEmail: "",
  requesterPhone: "",
  patientFirstName: "",
  patientLastName: "",
  patientDateOfBirth: "",
  patientSex: "",
  patientEmail: "",
  patientPhone: "",
  receiptEmail: "",
  addressLine1: "",
  addressLine2: "",
  community: "",
  accessDirections: "",
  clinicalNotes: "",
  selectedItemKeys: [],
  discussAtVisit: false,
  authorityAttested: false,
};

const BOOKING_STEP_LABELS = [
  "Choose collection",
  "Choose a time",
  "Patient details",
  "Visit details",
  "Choose services",
  "Review request",
] as const;

function itemKey(itemId: string, type: AppointmentRequestedItemType) {
  return `${type}:${itemId}`;
}

function money(value: number, currency: string) {
  return new Intl.NumberFormat("en-JM", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function appointmentStatusLabel(status: CollectionAppointmentStatus) {
  return (
    {
      [CollectionAppointmentStatus.PendingEmailVerification]:
        "Email verification required",
      [CollectionAppointmentStatus.PendingApproval]: "Awaiting staff approval",
      [CollectionAppointmentStatus.Confirmed]: "Confirmed",
      [CollectionAppointmentStatus.ChangeProposed]: "A new time was proposed",
      [CollectionAppointmentStatus.EnRoute]: "Collector en route",
      [CollectionAppointmentStatus.Arrived]: "Collector arrived",
      [CollectionAppointmentStatus.CollectionInProgress]:
        "Collection in progress",
      [CollectionAppointmentStatus.Completed]: "Completed",
      [CollectionAppointmentStatus.Declined]: "Declined",
      [CollectionAppointmentStatus.Cancelled]: "Cancelled",
      [CollectionAppointmentStatus.NoShow]: "No show",
      [CollectionAppointmentStatus.Expired]: "Expired",
    }[status] ?? "Appointment request"
  );
}

function TurnstileWidget({
  siteKey,
  onTokenChange,
}: {
  siteKey: string;
  onTokenChange: (token: string | null) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let widgetId: string | null = null;
    let disposed = false;
    const scriptId = "cloudflare-turnstile-script";

    const render = () => {
      if (
        disposed ||
        widgetId ||
        !containerRef.current ||
        !window.turnstile
      ) {
        return;
      }

      widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onTokenChange(token),
        "expired-callback": () => onTokenChange(null),
        "error-callback": () => {
          onTokenChange(null);
          setLoadFailed(true);
        },
        theme: "auto",
        size: "flexible",
      });
    };

    const onLoad = () => render();
    const onError = () => {
      onTokenChange(null);
      setLoadFailed(true);
    };
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    if (window.turnstile) {
      render();
    } else {
      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
    }

    return () => {
      disposed = true;
      script?.removeEventListener("load", onLoad);
      script?.removeEventListener("error", onError);
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
      onTokenChange(null);
    };
  }, [onTokenChange, siteKey]);

  return (
    <Paper className="review-card">
      <Text className="booking-section-label">Security check</Text>
      <Text size="sm" c="dimmed" mb="md">
        Complete this privacy-preserving check before submitting your request.
      </Text>
      <div ref={containerRef} />
      {loadFailed ? (
        <Alert color="red" mt="md">
          The security check could not load. Check your connection or content
          blocker, then refresh this page.
        </Alert>
      ) : null}
    </Paper>
  );
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    cache: "no-store",
    credentials: "omit",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const detail =
      payload?.detail ?? payload?.title ?? payload?.message ?? `HTTP ${response.status}`;
    throw new Error(detail);
  }
  return (await response.json()) as T;
}

function PublicHeader({ site }: { site: PublicMarketingSiteDto }) {
  return (
    <header className="booking-header">
      <Container size="xl">
        <Group
          className="public-header-row"
          justify="space-between"
          py="md"
          wrap="nowrap"
        >
          <Anchor className="brand-link public-header-brand" href="/">
            <NextGenBrand
              name={site.laboratoryName}
              subtitle={site.tagline}
              imageSize={44}
            />
          </Anchor>
          <Button
            component="a"
            href="/"
            variant="subtle"
            leftSection={<IconArrowLeft size={17} />}
          >
            <span className="public-header-back-long">Back to website</span>
            <span className="public-header-back-short">Home</span>
          </Button>
        </Group>
      </Container>
    </header>
  );
}

export function BookingPage({ site, style }: BookingPageProps) {
  const [configuration, setConfiguration] =
    useState<PublicAppointmentConfigurationDto | null>(null);
  const [form, setForm] = useState<BookingForm>(initialForm);
  const [active, setActive] = useState(0);
  const [slots, setSlots] = useState<PublicAppointmentSlotDto[]>([]);
  const [selectedSlotDate, setSelectedSlotDate] = useState("");
  const [visibleWeekStart, setVisibleWeekStart] = useState("");
  const [estimate, setEstimate] = useState<AppointmentEstimateDto | null>(null);
  const [created, setCreated] = useState<PublicAppointmentCreatedDto | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [access, setAccess] = useState<PublicAppointmentAccessDto | null>(null);
  const [managedStatus, setManagedStatus] =
    useState<PublicAppointmentStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `Book sample collection | ${site.laboratoryName}`;
    api<PublicAppointmentConfigurationDto>(
      "/api/public/appointments/configuration",
    )
      .then((result) => {
        setConfiguration(result);
        setLoading(false);
      })
      .catch((caught: unknown) => {
        setError(
          caught instanceof Error
            ? caught.message
            : "Online booking is temporarily unavailable.",
        );
        setLoading(false);
      });
  }, [site.laboratoryName]);

  const selectedItems = useMemo<AppointmentEstimateItemInput[]>(() => {
    return form.selectedItemKeys.map((key) => {
      const [type, itemId] = key.split(":");
      return {
        itemId,
        type: Number(type) as AppointmentRequestedItemType,
      };
    });
  }, [form.selectedItemKeys]);

  const selectedArea = configuration?.homeServiceAreas.find(
    (area) => area.id === form.homeServiceAreaId,
  );
  const selectedOffice = configuration?.offices.find(
    (office) => office.id === form.officeLocationId,
  );
  const selectedSlot = slots.find((slot) => slot.id === form.slotId);
  const isSelf = form.relationship === AppointmentRequesterRelationship.Self;
  const availableCatalog = useMemo(() => {
    if (!configuration) return [];
    return configuration.catalog.filter((item) =>
      form.type === CollectionAppointmentType.HomeCollection
        ? item.isHomeCollectionBookable
        : Boolean(
            form.officeLocationId &&
              item.orderableLocationIds.includes(form.officeLocationId),
          ),
    );
  }, [configuration, form.officeLocationId, form.type]);
  const availableCatalogKeys = useMemo(
    () => new Set(
      availableCatalog.map((item) => itemKey(item.id, item.type)),
    ),
    [availableCatalog],
  );
  const firstSlotDate = slots[0]?.localDate ?? "";
  const lastSlotDate = slots[slots.length - 1]?.localDate ?? "";
  const visibleWeek = visibleWeekStart
    ? calendarWeek(visibleWeekStart)
    : [];
  const selectedDateSlots = slots.filter(
    (slot) => slot.localDate === selectedSlotDate,
  );

  useEffect(() => {
    setForm((current) => {
      const selectedItemKeys = current.selectedItemKeys.filter((key) =>
        availableCatalogKeys.has(key),
      );
      return selectedItemKeys.length === current.selectedItemKeys.length
        ? current
        : { ...current, selectedItemKeys };
    });
  }, [availableCatalogKeys]);

  function updateFormField<K extends keyof BookingForm>(
    field: K,
    value: BookingForm[K],
  ) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function changeAppointmentType(value: string) {
    const type = Number(value) as CollectionAppointmentType;
    setSlots([]);
    setSelectedSlotDate("");
    setVisibleWeekStart("");
    setEstimate(null);
    setError(null);
    setForm((current) => resetLocationForAppointmentType(current, type));
  }

  function changeCollectionDestination(
    field: "homeServiceAreaId" | "officeLocationId",
    value: string | null,
  ) {
    setSlots([]);
    setSelectedSlotDate("");
    setVisibleWeekStart("");
    setEstimate(null);
    setError(null);
    setForm((current) => ({
      ...current,
      [field]: value ?? "",
      slotId: "",
    }));
  }

  async function loadSlots() {
    if (
      (form.type === CollectionAppointmentType.HomeCollection &&
        !form.homeServiceAreaId) ||
      (form.type === CollectionAppointmentType.InOfficeCollection &&
        !form.officeLocationId)
    ) {
      setError("Choose a service area or collection site first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        type: String(form.type),
      });
      if (form.homeServiceAreaId) {
        params.set("homeServiceAreaId", form.homeServiceAreaId);
      }
      if (form.officeLocationId) {
        params.set("officeLocationId", form.officeLocationId);
      }
      const result = await api<PublicAppointmentSlotDto[]>(
        `/api/public/appointments/slots?${params.toString()}`,
      );
      setSlots(result);
      setForm((current) => ({ ...current, slotId: "" }));
      const initialDate = result[0]?.localDate ?? "";
      setSelectedSlotDate(initialDate);
      setVisibleWeekStart(
        initialDate ? startOfCalendarWeek(initialDate) : "",
      );
      setActive(1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load times.");
    } finally {
      setBusy(false);
    }
  }

  async function calculateEstimate() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<AppointmentEstimateDto>(
        "/api/public/appointments/estimate",
        {
          method: "POST",
          body: JSON.stringify({
            type: form.type,
            homeServiceAreaId: form.homeServiceAreaId || null,
            officeLocationId: form.officeLocationId || null,
            items: selectedItems,
            discussTestsAtVisit: form.discussAtVisit,
          }),
        },
      );
      setEstimate(result);
      setActive(5);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not calculate estimate.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitBooking() {
    if (!configuration || !selectedSlot) return;
    setBusy(true);
    setError(null);
    const requestId = crypto.randomUUID();
    const patient = isSelf
      ? {
          firstName: form.requesterFirstName,
          lastName: form.requesterLastName,
          dateOfBirth: form.patientDateOfBirth,
          sex: form.patientSex || null,
          email: form.requesterEmail,
          phone: form.requesterPhone || null,
        }
      : {
          firstName: form.patientFirstName,
          lastName: form.patientLastName,
          dateOfBirth: form.patientDateOfBirth,
          sex: form.patientSex || null,
          email: form.patientEmail || null,
          phone: form.patientPhone || null,
        };
    const request: CreatePublicAppointmentRequest = {
      requestId,
      appointmentSlotId: selectedSlot.id,
      type: form.type,
      requesterRelationship: form.relationship,
      requester: {
        firstName: form.requesterFirstName,
        lastName: form.requesterLastName,
        email: form.requesterEmail,
        phone: form.requesterPhone || null,
      },
      patient,
      receiptEmail: form.receiptEmail || form.requesterEmail,
      proxyAuthorityAttested: isSelf || form.authorityAttested,
      homeServiceAreaId: form.homeServiceAreaId || null,
      officeLocationId: form.officeLocationId || null,
      addressLine1:
        form.type === CollectionAppointmentType.HomeCollection
          ? form.addressLine1
          : null,
      addressLine2: form.addressLine2 || null,
      community: form.community || null,
      parish: selectedArea?.parish ?? null,
      accessDirections: form.accessDirections || null,
      clinicalNotes: form.clinicalNotes || null,
      items: selectedItems,
      discussTestsAtVisit: form.discussAtVisit,
      turnstileToken,
    };
    try {
      const result = await api<PublicAppointmentCreatedDto>(
        "/api/public/appointments",
        { method: "POST", body: JSON.stringify(request) },
      );
      setCreated(result);
      setActive(6);
    } catch (caught) {
      if (configuration.turnstileEnabled) {
        setTurnstileToken(null);
        setTurnstileAttempt((current) => current + 1);
      }
      setError(
        caught instanceof Error ? caught.message : "Could not submit booking.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function verifyEmail() {
    if (!created) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<PublicAppointmentAccessDto>(
        "/api/public/appointments/verify",
        {
          method: "POST",
          body: JSON.stringify({
            requestId: created.requestId,
            verificationCode,
          }),
        },
      );
      setAccess(result);
      sessionStorage.setItem(
        `appointment:${result.appointmentNumber}`,
        result.managementToken,
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The verification code could not be confirmed.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function refreshStatus() {
    if (!access) return;
    setBusy(true);
    setError(null);
    try {
      setManagedStatus(
        await api<PublicAppointmentStatusDto>(
          "/api/public/appointments/status",
          {
            method: "POST",
            body: JSON.stringify({ managementToken: access.managementToken }),
          },
        ),
      );
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not refresh status.",
      );
    } finally {
      setBusy(false);
    }
  }

  function nextFromPeople() {
    if (
      !form.requesterFirstName ||
      !form.requesterLastName ||
      !form.requesterEmail ||
      !form.patientDateOfBirth ||
      (!isSelf && (!form.patientFirstName || !form.patientLastName)) ||
      (!isSelf && !form.authorityAttested)
    ) {
      setError("Complete the required requester and patient details.");
      return;
    }
    setError(null);
    setActive(3);
  }

  if (loading) {
    return (
      <Box className="booking-site" style={style}>
        <PublicHeader site={site} />
        <Box className="booking-loading">
          <Loader color="var(--site-primary)" />
          <Text>Loading available collection services…</Text>
        </Box>
      </Box>
    );
  }

  if (!configuration?.enabled) {
    return (
      <Box className="booking-site" style={style}>
        <PublicHeader site={site} />
        <Container size="sm" py={100}>
          <Paper className="booking-unavailable">
            <IconCalendarEvent size={40} />
            <Title order={1}>Online booking is coming soon</Title>
            <Text c="dimmed">
              Please contact the laboratory to arrange specimen collection.
            </Text>
            <Button component="a" href="/" mt="lg">
              Return to website
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="booking-site" style={style}>
      <PublicHeader site={site} />
      <main className="booking-main">
        <Container size="xl">
          <div className="booking-intro">
            <Text className="eyebrow">Collection, where you need it</Text>
            <Title order={1}>Book a sample collection</Title>
            <Text>
              Request a home visit or reserve a time at one of our collection
              sites. A team member will review and confirm every request.
            </Text>
            <Alert
              color="red"
              variant="light"
              icon={<IconStethoscope size={20} />}
              className="booking-critical-alert"
            >
              This service is for laboratory sample collection. It is not an
              emergency service or a medical consultation. If you need urgent
              care, contact emergency services.
            </Alert>
          </div>

          <Paper className="booking-shell">
            {!access ? (
              <>
                <div
                  className="booking-mobile-progress"
                  aria-label={
                    active >= BOOKING_STEP_LABELS.length
                      ? "Final step: verify email"
                      : `Step ${active + 1} of ${BOOKING_STEP_LABELS.length}: ${BOOKING_STEP_LABELS[active]}`
                  }
                >
                  <Group justify="space-between" gap="xs" wrap="nowrap">
                    <Text size="xs" fw={800}>
                      {active >= BOOKING_STEP_LABELS.length
                        ? "Final step"
                        : `Step ${active + 1} of ${BOOKING_STEP_LABELS.length}`}
                    </Text>
                    <Text size="sm" fw={700}>
                      {active >= BOOKING_STEP_LABELS.length
                        ? "Verify email"
                        : BOOKING_STEP_LABELS[active]}
                    </Text>
                  </Group>
                  <div className="booking-mobile-progress-track">
                    <span
                      style={{
                        width: `${Math.min(
                          ((active + 1) / BOOKING_STEP_LABELS.length) * 100,
                          100,
                        )}%`,
                      }}
                    />
                  </div>
                </div>
                <Stepper
                  active={active}
                  onStepClick={(step) => step < active && setActive(step)}
                  allowNextStepsSelect={false}
                  className="booking-stepper"
                >
                <Stepper.Step label="Collection" description="Where">
                  <Stack gap="xl" mt="xl">
                    <Radio.Group
                      value={String(form.type)}
                      onChange={changeAppointmentType}
                    >
                      <SimpleGrid cols={{ base: 1, sm: 2 }}>
                        <Paper
                          component="label"
                          className="booking-choice-card"
                          data-selected={
                            form.type === CollectionAppointmentType.HomeCollection
                          }
                        >
                          <Group align="flex-start" wrap="nowrap">
                            <Radio
                              value={String(
                                CollectionAppointmentType.HomeCollection,
                              )}
                            />
                            <Box>
                              <IconHomeHeart size={30} />
                              <Title order={3}>Home collection</Title>
                              <Text c="dimmed">
                                A trained collector comes to the patient.
                                Area-based visit fees apply.
                              </Text>
                            </Box>
                          </Group>
                        </Paper>
                        <Paper
                          component="label"
                          className="booking-choice-card"
                          data-selected={
                            form.type ===
                            CollectionAppointmentType.InOfficeCollection
                          }
                        >
                          <Group align="flex-start" wrap="nowrap">
                            <Radio
                              value={String(
                                CollectionAppointmentType.InOfficeCollection,
                              )}
                            />
                            <Box>
                              <IconBuildingHospital size={30} />
                              <Title order={3}>Visit a collection site</Title>
                              <Text c="dimmed">
                                Reserve a time at an enabled laboratory
                                location. No additional visit fee by default.
                              </Text>
                            </Box>
                          </Group>
                        </Paper>
                      </SimpleGrid>
                    </Radio.Group>

                    {form.type === CollectionAppointmentType.HomeCollection ? (
                      <Select
                        key="home-service-area"
                        label="Parish and service area"
                        required
                        searchable
                        placeholder="Choose the patient's area"
                        data={configuration.homeServiceAreas.map((area) => ({
                          value: area.id,
                          label: `${area.parish} — ${area.name} · ${money(
                            amountIncludingTax(area.fee, area.taxRate),
                            area.currency,
                          )} incl. GCT`,
                        }))}
                        value={form.homeServiceAreaId}
                        onChange={(value) =>
                          changeCollectionDestination(
                            "homeServiceAreaId",
                            value,
                          )
                        }
                      />
                    ) : (
                      <Select
                        key="office-location"
                        label="Collection site"
                        required
                        placeholder="Choose a location"
                        data={configuration.offices.map((office) => ({
                          value: office.id,
                          label: [
                            office.name,
                            office.city,
                            office.administrativeArea,
                          ]
                            .filter(Boolean)
                            .join(" — "),
                        }))}
                        value={form.officeLocationId}
                        onChange={(value) =>
                          changeCollectionDestination(
                            "officeLocationId",
                            value,
                          )
                        }
                      />
                    )}
                    <Group className="booking-actions" justify="flex-end">
                      <Button
                        onClick={loadSlots}
                        loading={busy}
                        rightSection={<IconArrowRight size={17} />}
                      >
                        Find available times
                      </Button>
                    </Group>
                  </Stack>
                </Stepper.Step>

                <Stepper.Step label="Time" description="Choose a slot">
                  <Stack gap="lg" mt="xl">
                    <Box>
                      <Text className="booking-section-label">
                        Available collection times
                      </Text>
                      <Text c="dimmed">
                        Times are shown in {configuration.timeZone}. Your time
                        is held briefly while you complete this request.
                      </Text>
                    </Box>
                    {slots.length ? (
                      <Paper className="booking-calendar" radius="lg">
                        <Group
                          className="booking-calendar-toolbar"
                          justify="space-between"
                          wrap="nowrap"
                        >
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            aria-label="Previous week"
                            disabled={
                              !firstSlotDate ||
                              addCalendarDays(visibleWeekStart, -7) <
                                startOfCalendarWeek(firstSlotDate)
                            }
                            onClick={() =>
                              setVisibleWeekStart((current) =>
                                addCalendarDays(current, -7),
                              )
                            }
                          >
                            <IconArrowLeft size={18} />
                          </Button>
                          <Box ta="center">
                            <Text fw={800}>Choose a day</Text>
                            <Text size="sm" c="dimmed">
                              {formatCalendarWeekRange(visibleWeekStart)}
                            </Text>
                          </Box>
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            aria-label="Next week"
                            disabled={
                              !lastSlotDate ||
                              addCalendarDays(visibleWeekStart, 7) >
                                startOfCalendarWeek(lastSlotDate)
                            }
                            onClick={() =>
                              setVisibleWeekStart((current) =>
                                addCalendarDays(current, 7),
                              )
                            }
                          >
                            <IconArrowRight size={18} />
                          </Button>
                        </Group>
                        <div
                          className="booking-week-days"
                          role="group"
                          aria-label="Available appointment days"
                        >
                          {visibleWeek.map((date) => {
                            const count = slots.filter(
                              (slot) => slot.localDate === date,
                            ).length;
                            return (
                              <button
                                type="button"
                                key={date}
                                className="booking-week-day"
                                data-selected={date === selectedSlotDate}
                                disabled={count === 0}
                                aria-pressed={date === selectedSlotDate}
                                onClick={() => {
                                  setSelectedSlotDate(date);
                                  setForm((current) => ({
                                    ...current,
                                    slotId: "",
                                  }));
                                }}
                              >
                                <span>
                                  {formatCalendarDay(date, {
                                    weekday: "short",
                                  })}
                                </span>
                                <strong>
                                  {formatCalendarDay(date, {
                                    day: "numeric",
                                  })}
                                </strong>
                                <small>
                                  {count ? `${count} time${count === 1 ? "" : "s"}` : "Full"}
                                </small>
                              </button>
                            );
                          })}
                        </div>
                        <SimpleGrid
                          cols={{ base: 1, sm: 2 }}
                          className="booking-time-picker"
                        >
                          <TextInput
                            type="date"
                            label="Jump to a date"
                            min={firstSlotDate}
                            max={lastSlotDate}
                            value={selectedSlotDate}
                            onChange={(event) => {
                              const date = event.currentTarget.value;
                              setSelectedSlotDate(date);
                              if (date) {
                                setVisibleWeekStart(
                                  startOfCalendarWeek(date),
                                );
                              }
                              setForm((current) => ({
                                ...current,
                                slotId: "",
                              }));
                            }}
                          />
                          <Select
                            label="Available times"
                            placeholder={
                              selectedDateSlots.length
                                ? "Choose a time"
                                : "No times on this day"
                            }
                            disabled={selectedDateSlots.length === 0}
                            value={form.slotId || null}
                            data={selectedDateSlots.map((slot) => ({
                              value: slot.id,
                              label: `${slot.localStartTime} – ${slot.localEndTime}`,
                            }))}
                            onChange={(slotId) =>
                              setForm((current) => ({
                                ...current,
                                slotId: slotId ?? "",
                              }))
                            }
                          />
                        </SimpleGrid>
                        {selectedSlot ? (
                          <Alert
                            mt="md"
                            color="green"
                            icon={<IconCheck size={18} />}
                          >
                            Selected: {formatCalendarDay(
                              selectedSlot.localDate,
                              {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                              },
                            )}, {selectedSlot.localStartTime}–
                            {selectedSlot.localEndTime}
                          </Alert>
                        ) : null}
                      </Paper>
                    ) : (
                      <Alert color="gray">
                        No public times are currently available for this
                        location. Try another location or contact the
                        laboratory.
                      </Alert>
                    )}
                    <Group className="booking-actions" justify="space-between">
                      <Button variant="subtle" onClick={() => setActive(0)}>
                        Back
                      </Button>
                      <Button
                        disabled={!form.slotId}
                        onClick={() => setActive(2)}
                        rightSection={<IconArrowRight size={17} />}
                      >
                        Continue
                      </Button>
                    </Group>
                  </Stack>
                </Stepper.Step>

                <Stepper.Step label="People" description="Requester & patient">
                  <Stack gap="xl" mt="xl">
                    <Select
                      label="Who are you booking for?"
                      required
                      data={[
                        {
                          value: String(AppointmentRequesterRelationship.Self),
                          label: "Myself",
                        },
                        {
                          value: String(
                            AppointmentRequesterRelationship.ParentOrGuardian,
                          ),
                          label: "My child or someone I legally represent",
                        },
                        {
                          value: String(
                            AppointmentRequesterRelationship.Relative,
                          ),
                          label: "A relative",
                        },
                        {
                          value: String(
                            AppointmentRequesterRelationship.Caregiver,
                          ),
                          label: "Someone I care for",
                        },
                        {
                          value: String(
                            AppointmentRequesterRelationship.OtherAuthorizedPerson,
                          ),
                          label: "Another person who authorized me",
                        },
                      ]}
                      value={String(form.relationship)}
                      onChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          relationship: Number(
                            value,
                          ) as AppointmentRequesterRelationship,
                        }))
                      }
                    />
                    <Box>
                      <Text className="booking-section-label">Your details</Text>
                      <SimpleGrid cols={{ base: 1, sm: 2 }}>
                        <TextInput
                          label="First name"
                          required
                          value={form.requesterFirstName}
                          onChange={(event) =>
                            updateFormField(
                              "requesterFirstName",
                              event.currentTarget.value,
                            )
                          }
                        />
                        <TextInput
                          label="Last name"
                          required
                          value={form.requesterLastName}
                          onChange={(event) =>
                            updateFormField(
                              "requesterLastName",
                              event.currentTarget.value,
                            )
                          }
                        />
                        <TextInput
                          label="Email"
                          type="email"
                          required
                          value={form.requesterEmail}
                          onChange={(event) => {
                            const value = event.currentTarget.value;
                            setForm((current) =>
                              updateRequesterEmail(current, value),
                            );
                          }}
                        />
                        <TextInput
                          label="Phone"
                          value={form.requesterPhone}
                          onChange={(event) =>
                            updateFormField(
                              "requesterPhone",
                              event.currentTarget.value,
                            )
                          }
                        />
                      </SimpleGrid>
                    </Box>
                    <Box>
                      <Text className="booking-section-label">
                        {isSelf ? "Patient details" : "Patient's details"}
                      </Text>
                      <SimpleGrid cols={{ base: 1, sm: 2 }}>
                        {!isSelf ? (
                          <>
                            <TextInput
                              label="Patient first name"
                              required
                              value={form.patientFirstName}
                              onChange={(event) =>
                                updateFormField(
                                  "patientFirstName",
                                  event.currentTarget.value,
                                )
                              }
                            />
                            <TextInput
                              label="Patient last name"
                              required
                              value={form.patientLastName}
                              onChange={(event) =>
                                updateFormField(
                                  "patientLastName",
                                  event.currentTarget.value,
                                )
                              }
                            />
                          </>
                        ) : null}
                        <TextInput
                          label="Patient date of birth"
                          type="date"
                          required
                          value={form.patientDateOfBirth}
                          onChange={(event) =>
                            updateFormField(
                              "patientDateOfBirth",
                              event.currentTarget.value,
                            )
                          }
                        />
                        <Select
                          label="Sex"
                          clearable
                          data={[...PATIENT_SEX_OPTIONS]}
                          value={form.patientSex}
                          onChange={(value) =>
                            setForm((current) => ({
                              ...current,
                              patientSex: value ?? "",
                            }))
                          }
                        />
                        {!isSelf ? (
                          <>
                            <TextInput
                              label="Patient email"
                              type="email"
                              value={form.patientEmail}
                              onChange={(event) =>
                                updateFormField(
                                  "patientEmail",
                                  event.currentTarget.value,
                                )
                              }
                            />
                            <TextInput
                              label="Patient phone"
                              value={form.patientPhone}
                              onChange={(event) =>
                                updateFormField(
                                  "patientPhone",
                                  event.currentTarget.value,
                                )
                              }
                            />
                          </>
                        ) : null}
                      </SimpleGrid>
                    </Box>
                    {!isSelf ? (
                      <Checkbox
                        checked={form.authorityAttested}
                        onChange={(event) =>
                          updateFormField(
                            "authorityAttested",
                            event.currentTarget.checked,
                          )
                        }
                        label="I confirm that I am authorized to request this visit for the patient. Laboratory staff will reconfirm consent before collection."
                      />
                    ) : null}
                    <Group className="booking-actions" justify="space-between">
                      <Button variant="subtle" onClick={() => setActive(1)}>
                        Back
                      </Button>
                      <Button onClick={nextFromPeople}>Continue</Button>
                    </Group>
                  </Stack>
                </Stepper.Step>

                <Stepper.Step label="Details" description="Visit information">
                  <Stack gap="lg" mt="xl">
                    {form.type === CollectionAppointmentType.HomeCollection ? (
                      <>
                        <Text className="booking-section-label">
                          Collection address
                        </Text>
                        <TextInput
                          label="Street address"
                          required
                          value={form.addressLine1}
                          onChange={(event) =>
                            updateFormField(
                              "addressLine1",
                              event.currentTarget.value,
                            )
                          }
                        />
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                          <TextInput
                            label="Apartment / unit"
                            value={form.addressLine2}
                            onChange={(event) =>
                              updateFormField(
                                "addressLine2",
                                event.currentTarget.value,
                              )
                            }
                          />
                          <TextInput
                            label="Community"
                            required
                            value={form.community}
                            onChange={(event) =>
                              updateFormField(
                                "community",
                                event.currentTarget.value,
                              )
                            }
                          />
                        </SimpleGrid>
                        <Textarea
                          label="Access directions"
                          description="Gate, landmark, parking, accessibility, or arrival instructions."
                          value={form.accessDirections}
                          onChange={(event) =>
                            updateFormField(
                              "accessDirections",
                              event.currentTarget.value,
                            )
                          }
                        />
                      </>
                    ) : (
                      <Alert icon={<IconMapPin size={20} />}>
                        Your collection is requested at{" "}
                        <strong>{selectedOffice?.name}</strong>.
                      </Alert>
                    )}
                    <Textarea
                      label="Notes for the collection team"
                      description="Optional. Do not use this form for urgent medical concerns."
                      value={form.clinicalNotes}
                      onChange={(event) =>
                        updateFormField(
                          "clinicalNotes",
                          event.currentTarget.value,
                        )
                      }
                    />
                    <TextInput
                      label="Receipt email"
                      type="email"
                      required
                      value={form.receiptEmail}
                      onChange={(event) =>
                        updateFormField(
                          "receiptEmail",
                          event.currentTarget.value,
                        )
                      }
                    />
                    <Group className="booking-actions" justify="space-between">
                      <Button variant="subtle" onClick={() => setActive(2)}>
                        Back
                      </Button>
                      <Button
                        disabled={
                          form.type ===
                            CollectionAppointmentType.HomeCollection &&
                          (!form.addressLine1 || !form.community)
                        }
                        onClick={() => setActive(4)}
                      >
                        Continue
                      </Button>
                    </Group>
                  </Stack>
                </Stepper.Step>

                <Stepper.Step label="Services" description="Tests requested">
                  <Stack gap="lg" mt="xl">
                    <Alert color="blue" variant="light">
                      These selections create an estimate only. Staff will
                      confirm the patient, specimen requirements, physician
                      orders, final tests, and amount due before collection.
                    </Alert>
                    <Checkbox
                      checked={form.discussAtVisit}
                      onChange={(event) =>
                        updateFormField(
                          "discussAtVisit",
                          event.currentTarget.checked,
                        )
                      }
                      label="I want to discuss which tests are needed at the visit"
                    />
                    {availableCatalog.length === 0 ? (
                      <Alert color="orange" variant="light">
                        No tests or panels are currently available for this
                        collection option. Select “discuss which tests are
                        needed” to continue, or contact the laboratory.
                      </Alert>
                    ) : null}
                    <div className="public-catalog">
                      {Array.from(
                        new Set(
                          availableCatalog.map((item) => item.category),
                        ),
                      ).map((category) => (
                        <Box key={category} className="catalog-group">
                          <Text className="booking-section-label">
                            {category}
                          </Text>
                          <Stack gap="xs">
                            {availableCatalog
                              .filter((item) => item.category === category)
                              .map((item) => {
                                const key = itemKey(item.id, item.type);
                                return (
                                  <Paper
                                    component="label"
                                    className="catalog-option"
                                    key={key}
                                  >
                                    <Checkbox
                                      checked={form.selectedItemKeys.includes(key)}
                                      onChange={(event) => {
                                        const checked = event.currentTarget.checked;
                                        setForm((current) => ({
                                          ...current,
                                          selectedItemKeys: checked
                                            ? [...current.selectedItemKeys, key]
                                            : current.selectedItemKeys.filter(
                                                (selected) => selected !== key,
                                              ),
                                        }));
                                      }}
                                    />
                                    <Box>
                                      <Group gap="xs">
                                        <Text fw={800}>
                                          {item.code} · {item.name}
                                        </Text>
                                        {item.type ===
                                        AppointmentRequestedItemType.Panel ? (
                                          <Badge variant="light">Panel</Badge>
                                        ) : null}
                                        {item.requiresPhysicianOrder ? (
                                          <Badge color="orange" variant="light">
                                            Physician order required
                                          </Badge>
                                        ) : null}
                                      </Group>
                                      {item.description ? (
                                        <Text size="sm" c="dimmed">
                                          {item.description}
                                        </Text>
                                      ) : null}
                                    </Box>
                                    <Text fw={800} className="catalog-price">
                                      {money(
                                        amountIncludingTax(
                                          item.price,
                                          item.taxRate,
                                        ),
                                        item.currency,
                                      )}
                                    </Text>
                                  </Paper>
                                );
                              })}
                          </Stack>
                        </Box>
                      ))}
                    </div>
                    <Group className="booking-actions" justify="space-between">
                      <Button variant="subtle" onClick={() => setActive(3)}>
                        Back
                      </Button>
                      <Button
                        disabled={
                          !form.discussAtVisit &&
                          form.selectedItemKeys.length === 0
                        }
                        loading={busy}
                        onClick={calculateEstimate}
                      >
                        Review estimate
                      </Button>
                    </Group>
                  </Stack>
                </Stepper.Step>

                <Stepper.Step label="Review" description="Estimate & submit">
                  <Stack gap="xl" mt="xl">
                    <SimpleGrid cols={{ base: 1, md: 2 }}>
                      <Paper className="review-card">
                        <Text className="booking-section-label">
                          Visit summary
                        </Text>
                        <Text fw={800}>
                          {form.type ===
                          CollectionAppointmentType.HomeCollection
                            ? `Home collection · ${selectedArea?.name}`
                            : `In-office collection · ${selectedOffice?.name}`}
                        </Text>
                        <Text>
                          {selectedSlot?.localDate} ·{" "}
                          {selectedSlot?.localStartTime}–
                          {selectedSlot?.localEndTime}
                        </Text>
                        <Text c="dimmed" size="sm">
                          Requested for{" "}
                          {isSelf
                            ? `${form.requesterFirstName} ${form.requesterLastName}`
                            : `${form.patientFirstName} ${form.patientLastName}`}
                        </Text>
                      </Paper>
                      <Paper className="review-card">
                        <Text className="booking-section-label">
                          Estimated amount due at visit
                        </Text>
                        {estimate?.lines.map((line) => (
                          <Group key={`${line.code}:${line.name}`} justify="space-between">
                            <Text size="sm">{line.name}</Text>
                            <Text size="sm" fw={700}>
                              {money(line.total, estimate.currency)}
                            </Text>
                          </Group>
                        ))}
                        <Divider my="sm" />
                        <Group justify="space-between">
                          <Text>GCT</Text>
                          <Text>{money(estimate?.tax ?? 0, estimate?.currency ?? "JMD")}</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text fw={800}>Estimated total</Text>
                          <Title order={3}>
                            {money(
                              estimate?.total ?? 0,
                              estimate?.currency ?? "JMD",
                            )}
                          </Title>
                        </Group>
                      </Paper>
                    </SimpleGrid>
                    <Alert
                      color="red"
                      variant="light"
                      icon={<IconShieldCheck size={20} />}
                      className="booking-critical-alert"
                    >
                      This is not a confirmed appointment or final bill. Prices
                      are recalculated when staff confirm the actual tests.
                      Payment is by cash or card at the visit. No card details
                      are collected here.
                    </Alert>
                    {configuration.turnstileEnabled &&
                    configuration.turnstileSiteKey ? (
                      <TurnstileWidget
                        key={turnstileAttempt}
                        siteKey={configuration.turnstileSiteKey}
                        onTokenChange={setTurnstileToken}
                      />
                    ) : null}
                    <Group className="booking-actions" justify="space-between">
                      <Button variant="subtle" onClick={() => setActive(4)}>
                        Back
                      </Button>
                      <Button
                        loading={busy}
                        disabled={
                          configuration.turnstileEnabled && !turnstileToken
                        }
                        onClick={submitBooking}
                        rightSection={<IconArrowRight size={17} />}
                      >
                        Submit appointment request
                      </Button>
                    </Group>
                  </Stack>
                </Stepper.Step>

                <Stepper.Completed>
                  <Stack gap="xl" mt="xl">
                    <Box className="verification-mark">
                      <IconMailCheck size={38} />
                    </Box>
                    <Box>
                      <Title order={2}>Check your email</Title>
                      <Text c="dimmed" mt="xs">
                        We sent a one-time code to {created?.maskedEmail}.
                        Enter it below to reserve the requested time for staff
                        review.
                      </Text>
                    </Box>
                    <TextInput
                      label="Email verification code"
                      required
                      autoComplete="one-time-code"
                      value={verificationCode}
                      onChange={(event) =>
                        setVerificationCode(
                          event.currentTarget.value.toUpperCase(),
                        )
                      }
                    />
                    <Button
                      loading={busy}
                      disabled={!verificationCode.trim()}
                      onClick={verifyEmail}
                      leftSection={<IconCheck size={17} />}
                    >
                      Verify appointment request
                    </Button>
                  </Stack>
                </Stepper.Completed>
                </Stepper>
              </>
            ) : (
              <Stack gap="xl" className="booking-confirmed">
                <Box className="verification-mark">
                  <IconCheck size={38} />
                </Box>
                <Box>
                  <Text className="eyebrow">Request verified</Text>
                  <Title order={1}>{access.appointmentNumber}</Title>
                  <Text c="dimmed" mt="sm">
                    Your requested slot is reserved while the laboratory team
                    reviews the visit. This is not yet a confirmed
                    appointment.
                  </Text>
                </Box>
                <Alert color="green">
                  {appointmentStatusLabel(
                    managedStatus?.status ?? access.status,
                  )}
                </Alert>
                {managedStatus ? (
                  <Paper className="status-summary">
                    <Group justify="space-between">
                      <Text>Location</Text>
                      <Text fw={700}>{managedStatus.locationSummary}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text>Estimated total</Text>
                      <Text fw={700}>
                        {money(
                          managedStatus.estimate.total,
                          managedStatus.estimate.currency,
                        )}
                      </Text>
                    </Group>
                  </Paper>
                ) : null}
                <Group className="booking-confirmed-actions">
                  <Button loading={busy} onClick={refreshStatus}>
                    Refresh request status
                  </Button>
                  <Button component="a" href="/" variant="subtle">
                    Return to website
                  </Button>
                </Group>
                <Text size="sm" c="dimmed">
                  Keep this page open until you receive the confirmation
                  email. For privacy, the management token is stored only in
                  this browser session.
                </Text>
              </Stack>
            )}
            {error ? (
              <Alert mt="xl" color="red" title="We could not continue">
                {error}
              </Alert>
            ) : null}
          </Paper>
        </Container>
      </main>
    </Box>
  );
}
